import React from 'react';
import { useTranslation } from 'react-i18next';
import ThemePreview from '@common/components/theme-preview';
import Icon from '@common/components/icon';
import type { EditableLocation } from './locations-editor';
import type { TrialResearchPreview } from '../../types';

type Props = {
  name: string;
  preview: TrialResearchPreview;
  locations: EditableLocation[];
  onEditLocations?: () => void;
};

/**
 * The visitor's app before it exists: the shared theme preview with their
 * name, icon and colours, plus the locations that will become networks.
 */
export const AppPreview = ({
  name, preview, locations, onEditLocations,
}: Props) => {
  const { t } = useTranslation();
  const main = preview.colors?.main ?? '#19214D';
  const button = preview.colors?.button ?? '#007AFF';
  const selected = locations.filter((location) => location.selected);

  return (
    <div className="FreeTrial__AppPreview">
      <style>
        {`.FreeTrial__AppPreview *[fill="primaryColor"] { fill: ${main}; }
          .FreeTrial__AppPreview *[fill="buttonColor"] { fill: ${button}; }`}
      </style>
      <div className="FreeTrial__AppPreview__Screen">
        <ThemePreview
          loggedUser={{ full_name: '', profile_img: null }}
          organisation={{ name, brand_icon: preview.icon_url || preview.logo_url, theme: {} }}
        />
      </div>
      <div className="FreeTrial__Card FreeTrial__AppPreview__Locations">
        <div className="FreeTrial__AppPreview__Locations__Header">
          <h6>{t('authentication:trial_preview_locations_title')}</h6>
          <span className="FreeTrial__Hint">
            <Icon type="place" size="xsmall" />
            {t('authentication:trial_preview_locations_found', { count: selected.length })}
          </span>
        </div>
        {selected.slice(0, 5).map((location) => (
          <div key={location.id} className="FreeTrial__AppPreview__Location">
            <span className="FreeTrial__AppPreview__Location__Icon" style={{ backgroundColor: main }}>
              {location.name.charAt(0)}
            </span>
            <span className="FreeTrial__AppPreview__Location__Name">{location.name}</span>
            {location.is_head_office && <span className="FreeTrial__Hint">{t('authentication:trial_preview_head_office')}</span>}
          </div>
        ))}
        {onEditLocations && (
          <div className="FreeTrial__Hint FreeTrial__AppPreview__Caption">
            <a onClick={onEditLocations} role="link">{t('authentication:trial_preview_locations_edit_title')}</a>
          </div>
        )}
      </div>
    </div>
  );
};
